const CART_DRAWER_RECOMMENDATION_REQUEST_COOLDOWN_MS = 8000;
const CART_DRAWER_RECOMMENDATION_RATE_LIMIT_BACKOFF_MS = 60000;
const CART_DRAWER_RECOMMENDATIONS_SESSION_STORAGE_KEY = 'omtechAuCartDrawerRecommendations';

function readCartDrawerRecommendationsSessionState() {
  try {
    const storedValue = window.sessionStorage.getItem(CART_DRAWER_RECOMMENDATIONS_SESSION_STORAGE_KEY);
    if (!storedValue) return null;

    return JSON.parse(storedValue);
  } catch (error) {
    return null;
  }
}

function persistCartDrawerRecommendationsStore(store) {
  try {
    const serializedState = JSON.stringify({
      cache: Object.fromEntries(store.cache),
      lastRequestedAt: Object.fromEntries(store.lastRequestedAt),
      rateLimitedUntil: Object.fromEntries(store.rateLimitedUntil),
    });

    window.sessionStorage.setItem(CART_DRAWER_RECOMMENDATIONS_SESSION_STORAGE_KEY, serializedState);
  } catch (error) {
    // Ignore storage failures so recommendations still work in-memory.
  }
}

function getCartDrawerRecommendationsStore() {
  if (!window.__cartDrawerRecommendationsStoreAu) {
    const sessionState = readCartDrawerRecommendationsSessionState();

    window.__cartDrawerRecommendationsStoreAu = {
      cache: new Map(Object.entries(sessionState?.cache || {})),
      inFlightRequests: new Map(),
      lastRequestedAt: new Map(
        Object.entries(sessionState?.lastRequestedAt || {}).map(([key, value]) => [key, Number(value) || 0])
      ),
      rateLimitedUntil: new Map(
        Object.entries(sessionState?.rateLimitedUntil || {}).map(([key, value]) => [key, Number(value) || 0])
      ),
    };
  }

  return window.__cartDrawerRecommendationsStoreAu;
}

function setLatestCartDrawerRecommendationSeed(productId) {
  if (!productId) return;
  window.__cartDrawerLatestRecommendationProductIdAu = String(productId);
}

function getLatestCartDrawerRecommendationSeed() {
  return window.__cartDrawerLatestRecommendationProductIdAu || null;
}

class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    this.handleDocumentClick = this.onDocumentClick.bind(this);
    this.handleProductAdded = this.onProductAdded.bind(this);
    this.handleCartUpdated = this.onCartUpdated.bind(this);
    this.handleSeedUpdated = this.onSeedUpdated.bind(this);

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('ajaxProduct:added', this.handleProductAdded);
    document.addEventListener('cart:updated', this.handleCartUpdated);
    document.addEventListener('cart-drawer-recommendation:seed', this.handleSeedUpdated);

    this.defaultProductId = this.dataset.defaultProductId || '';
    this.hideContainer();

    if (this.defaultProductId) {
      this.loadRecommendationsForProduct(this.defaultProductId);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('ajaxProduct:added', this.handleProductAdded);
    document.removeEventListener('cart:updated', this.handleCartUpdated);
    document.removeEventListener('cart-drawer-recommendation:seed', this.handleSeedUpdated);
  }

  buildRecommendationUrl(productId) {
    const baseUrl = this.dataset.baseUrl;
    if (!baseUrl || !productId) return null;

    const requestUrl = new URL(baseUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', this.dataset.sectionId || 'cart-drawer-recommendations');
    requestUrl.searchParams.set('product_id', productId);
    requestUrl.searchParams.set('limit', this.dataset.limit || '20');
    return requestUrl.toString();
  }

  getCachedRecommendationMarkup(recommendationKey) {
    if (!recommendationKey) return null;

    const cacheEntry = getCartDrawerRecommendationsStore().cache.get(recommendationKey);
    return cacheEntry?.markup || null;
  }

  renderRecommendationMarkup(markup) {
    if (!markup) {
      this.showFallbackRecommendations();
      return;
    }

    this.innerHTML = markup;
    this.hidden = false;
    this.dataset.loaded = 'true';
    this.initializeCards();
  }

  async fetchRecommendationsMarkup(url) {
    const response = await fetch(url);

    if (!response.ok) {
      const error = new Error(`Failed to load recommendations: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const text = await response.text();
    const html = document.createElement('div');
    html.innerHTML = text;

    const recommendationSection = html.querySelector('[data-cart-drawer-recommendations-section]');
    if (!recommendationSection) return null;

    const markup = recommendationSection.innerHTML.trim();
    return markup.length ? markup : null;
  }

  async loadRecommendationsForProduct(productId) {
    if (!productId || this.isLoadingRecommendations) return;

    const url = this.buildRecommendationUrl(productId);
    if (!url) {
      this.showFallbackRecommendations();
      return;
    }

    const recommendationKey = String(productId);
    const recommendationsStore = getCartDrawerRecommendationsStore();
    const now = Date.now();
    const cachedMarkup = this.getCachedRecommendationMarkup(recommendationKey);

    this.currentProductId = recommendationKey;

    if (cachedMarkup) {
      this.renderRecommendationMarkup(cachedMarkup);
      return;
    }

    const rateLimitedUntil = recommendationsStore.rateLimitedUntil.get(recommendationKey) || 0;
    if (rateLimitedUntil > now) {
      this.showFallbackRecommendations();
      return;
    }

    const inFlightRequest = recommendationsStore.inFlightRequests.get(recommendationKey);
    if (inFlightRequest) {
      this.isLoadingRecommendations = true;

      try {
        const markup = await inFlightRequest;
        if (markup) {
          this.renderRecommendationMarkup(markup);
        } else {
          this.showFallbackRecommendations();
        }
      } catch (error) {
        const fallbackMarkup = this.getCachedRecommendationMarkup(recommendationKey);
        if (fallbackMarkup) {
          this.renderRecommendationMarkup(fallbackMarkup);
        } else {
          this.showFallbackRecommendations();
        }
      } finally {
        this.isLoadingRecommendations = false;
      }

      return;
    }

    const lastRequestedAt = recommendationsStore.lastRequestedAt.get(recommendationKey) || 0;
    if (now - lastRequestedAt < CART_DRAWER_RECOMMENDATION_REQUEST_COOLDOWN_MS) {
      this.showFallbackRecommendations();
      return;
    }

    this.isLoadingRecommendations = true;
    const requestPromise = this.fetchRecommendationsMarkup(url);
    recommendationsStore.lastRequestedAt.set(recommendationKey, now);
    recommendationsStore.inFlightRequests.set(recommendationKey, requestPromise);
    persistCartDrawerRecommendationsStore(recommendationsStore);

    try {
      const markup = await requestPromise;

      if (markup) {
        recommendationsStore.cache.set(recommendationKey, {
          markup,
          cachedAt: Date.now(),
        });
        persistCartDrawerRecommendationsStore(recommendationsStore);
        this.renderRecommendationMarkup(markup);
        return;
      }

      this.showFallbackRecommendations();
    } catch (error) {
      if (error.status === 429) {
        recommendationsStore.rateLimitedUntil.set(
          recommendationKey,
          Date.now() + CART_DRAWER_RECOMMENDATION_RATE_LIMIT_BACKOFF_MS
        );
        persistCartDrawerRecommendationsStore(recommendationsStore);
      }

      const fallbackMarkup = this.getCachedRecommendationMarkup(recommendationKey);
      if (fallbackMarkup) {
        this.renderRecommendationMarkup(fallbackMarkup);
      } else {
        this.showFallbackRecommendations();
      }
    } finally {
      recommendationsStore.inFlightRequests.delete(recommendationKey);
      this.isLoadingRecommendations = false;
    }
  }

  showFallbackRecommendations() {
    const fallbackRecommendations = this.querySelector('[data-fallback-recommendations]');

    if (!fallbackRecommendations) {
      this.hideContainer();
      return;
    }

    this.hidden = false;
    fallbackRecommendations.hidden = false;
    this.initializeCards();
  }

  hideContainer() {
    this.hidden = true;
  }

  initializeCards() {
    this.cards = Array.from(this.querySelectorAll('[data-recommendation-card]'));
    this.cards.forEach((card) => this.initializeCard(card));
  }

  initializeCard(card) {
    if (card.dataset.initialized === 'true') return;

    const variantsNode = card.querySelector('[data-product-variants]');
    if (!variantsNode) return;

    let variants = [];

    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch (error) {
      return;
    }

    if (!variants.length) return;

    const dropdown = card.querySelector('[data-recommendation-dropdown]');
    const variantTrigger = card.querySelector('[data-recommendation-variant-trigger]');
    const variantLabel = card.querySelector('[data-recommendation-variant-label]');
    const variantMenu = card.querySelector('[data-recommendation-variant-menu]');
    const variantOptions = Array.from(card.querySelectorAll('[data-recommendation-variant-option]'));
    const variantInput = card.querySelector('[data-recommendation-variant-input]');
    const submitButton = card.querySelector('[name="add"]');
    const submitText = card.querySelector('[data-add-to-cart-text]');
    const currentPrice = card.querySelector('[data-price-current]');
    const comparePrice = card.querySelector('[data-price-compare]');
    const savePrice = card.querySelector('[data-price-save]');
    const addForm = card.querySelector('[data-recommendation-add-form]');
    const spinner = card.querySelector('[data-recommendation-spinner]');

    const updateCard = (variantId) => {
      const nextVariant = variants.find((variant) => String(variant.id) === String(variantId));
      if (!nextVariant) return;

      variantInput.value = nextVariant.id;
      currentPrice.textContent = nextVariant.price_text;
      if (variantLabel) variantLabel.textContent = nextVariant.title || '';

      if (nextVariant.compare_at_price_text) {
        currentPrice.classList.add('is-sale');
        comparePrice.textContent = nextVariant.compare_at_price_text;
        comparePrice.hidden = false;
        savePrice.textContent = nextVariant.save_text;
        savePrice.hidden = false;
      } else {
        currentPrice.classList.remove('is-sale');
        comparePrice.textContent = '';
        comparePrice.hidden = true;
        savePrice.textContent = '';
        savePrice.hidden = true;
      }

      if (nextVariant.available) {
        submitButton.disabled = false;
        submitText.textContent = '+ Add';
      } else {
        submitButton.disabled = true;
        submitText.textContent = 'Sold out';
      }

      variantOptions.forEach((option) => {
        option.classList.toggle('is-selected', String(option.dataset.variantId) === String(nextVariant.id));
      });
    };

    if (dropdown && variantTrigger && variantMenu && variantOptions.length) {
      variantTrigger.addEventListener('click', () => {
        const isOpen = dropdown.classList.toggle('is-open');
        variantTrigger.setAttribute('aria-expanded', String(isOpen));
        variantMenu.hidden = !isOpen;
      });

      variantOptions.forEach((option) => {
        option.addEventListener('click', () => {
          updateCard(option.dataset.variantId);
          dropdown.classList.remove('is-open');
          variantTrigger.setAttribute('aria-expanded', 'false');
          variantMenu.hidden = true;
        });
      });
    }

    if (addForm && submitButton && variantInput) {
      submitButton.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (submitButton.disabled || submitButton.dataset.loading === 'true') return;

        submitButton.dataset.loading = 'true';
        submitButton.disabled = true;
        submitButton.classList.add('is-loading', 'btn--loading');
        if (spinner) spinner.classList.remove('hidden');

        const variantId = variantInput.value;
        const payload = {
          items: [
            {
              id: variantId,
              quantity: 1
            }
          ]
        };

        try {
          const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            body: JSON.stringify(payload),
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          const result = await response.json();

          if (result.status === 422 || result.status === 'bad_request' || result.status) {
            submitText.textContent = result.description || 'Unavailable';
            return;
          }

          const addedItem = Array.isArray(result.items) ? result.items[0] : null;
          const nextProductId = addedItem?.product_id || result.product_id || addForm.dataset.productId;

          if (nextProductId) {
            setLatestCartDrawerRecommendationSeed(nextProductId);
          }

          new theme.HeaderCart();
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: result,
              productId: nextProductId,
              addToCartBtn: submitButton
            }
          }));
        } catch (error) {
          submitText.textContent = 'Unavailable';
        } finally {
          delete submitButton.dataset.loading;
          submitButton.classList.remove('is-loading', 'btn--loading');
          if (spinner) spinner.classList.add('hidden');

          const currentVariant = variants.find((variant) => String(variant.id) === String(variantInput.value));
          submitButton.disabled = !currentVariant?.available;
        }
      });
    }

    updateCard(variantInput.value || variants[0].id);
    card.dataset.initialized = 'true';
  }

  onDocumentClick(event) {
    const openDropdowns = this.querySelectorAll('[data-recommendation-dropdown].is-open');
    openDropdowns.forEach((dropdown) => {
      if (dropdown.contains(event.target)) return;
      dropdown.classList.remove('is-open');
      const trigger = dropdown.querySelector('[data-recommendation-variant-trigger]');
      const menu = dropdown.querySelector('[data-recommendation-variant-menu]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (menu) menu.hidden = true;
    });
  }

  onProductAdded(event) {
    const productId = event.detail?.productId || getLatestCartDrawerRecommendationSeed();
    if (!productId) return;

    this.defaultProductId = String(productId);
    this.loadRecommendationsForProduct(this.defaultProductId);
  }

  onCartUpdated(event) {
    const cart = event.detail?.cart;
    if (!cart || !Array.isArray(cart.items)) return;

    if (!cart.items.length) {
      this.hideContainer();
      return;
    }

    const nextSeedProductId = cart.items[0].product_id;
    this.defaultProductId = String(nextSeedProductId);
    setLatestCartDrawerRecommendationSeed(nextSeedProductId);
    this.loadRecommendationsForProduct(this.defaultProductId);
  }

  onSeedUpdated(event) {
    const productId = event.detail?.productId;
    if (!productId) return;

    setLatestCartDrawerRecommendationSeed(productId);
    this.defaultProductId = String(productId);
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
